import { type JSX, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * The primary action uses the brand accent. Ink still anchors the product mark and type, while
 * the one filled accent action tells an operator what moves the current task forward.
 *
 * Two details that are easy to lose and hard to get back:
 *
 *   · `active:scale-[0.98]` — the press has to be felt. Without it every click leaves a beat of
 *     doubt about whether the interface heard it, and users double-submit.
 *   · focus remains a 2px ACCENT outline with separation from the control. On the primary button,
 *     that gap keeps a same-hue focus outline visible.
 *
 * Heights come from --size-control-*, shared with Input and Select, so a row of mixed controls
 * aligns by definition rather than by coincidence.
 */
const button = tv({
  base: [
    'inline-flex shrink-0 items-center justify-center gap-control-gap rounded-action',
    'font-medium whitespace-nowrap select-none',
    'transition-[background-color,color,border-color,box-shadow,transform]',
    'duration-(--duration-press) ease-out',
    'active:scale-[0.98]',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'disabled:pointer-events-none disabled:opacity-45',
    // Heights follow the touch tokens on a phone; an icon-only button needs the width too.
    'max-md:min-w-(--size-touch)',
    // Icons passed as children should never dictate the control's height.
    '[&_svg]:shrink-0',
  ],
  variants: {
    variant: {
      primary: 'bg-brand-700 text-text-inverse hover:bg-brand-800',
      /** The default for anything that is not THE action on the screen. */
      secondary:
        'border border-border bg-surface text-text hover:border-border-strong hover:bg-surface-sunken',
      /** A quieter brand treatment for a highlighted action that is not primary. */
      accent: 'bg-accent-soft text-brand-700 hover:bg-brand-100',
      ghost: 'text-text-muted hover:bg-surface-sunken hover:text-text',
      danger: 'bg-status-critical text-text-inverse hover:brightness-92',
    },
    /* Icon size belongs to the size variant, not to the base. A 16px icon in a 28px control
       eats half its height and is why the dense toolbars read as oversized; every caller was
       otherwise repeating `[&_svg]:size-3.5` to undo it. */
    size: {
      sm: 'h-(--size-control-sm) px-control-x-sm text-body [&_svg]:size-3.5',
      md: 'h-(--size-control-md) px-control-x-md text-body [&_svg]:size-4',
      /** Shop-floor default — usable with gloves on. */
      touch: 'h-(--size-control-lg) px-control-x-lg text-ui [&_svg]:size-4',
      /** Square, for a lone icon. Matches `md` height so it sits in a control row. */
      icon: 'size-(--size-control-md) [&_svg]:size-4',
    },
  },
  defaultVariants: { variant: 'primary', size: 'md' },
})

export type ButtonProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof button>

export const Button = (props: ButtonProps) => {
  // Props must NOT be destructured in Solid — that reads them once and freezes the value.
  // splitProps keeps `rest` reactive.
  const [local, rest] = splitProps(props, ['variant', 'size', 'class'])

  return (
    <button
      type="button"
      {...rest}
      class={cn(button({ variant: local.variant, size: local.size }), local.class)}
    />
  )
}
